/** Reports failures the user is not told about, each at most once; reporting never throws. */

import type { IApplicationInsights } from '@microsoft/applicationinsights-web'
import { isUnexpectedError, toError } from '~/utils/http/api-error'

type ExceptionSink = Pick<IApplicationInsights, 'trackException'> | undefined

// Errors already sent, by this module or by a reporter that registered through markReported.
const reported = new WeakSet<object>()
// Errors waiting for the end of the current tick, so one that is shown twice is queued once.
const queued = new WeakSet<object>()

/**
 * Registers an error another reporter has sent or is about to send, so it is not reported a
 * second time when it is shown.
 */
export const markReported = (error: unknown) => {
  if (typeof error === 'object' && error !== null) {
    reported.add(error)
  }
}

const send = (exception: Error, sink: ExceptionSink, properties: Record<string, unknown>) => {
  if (reported.has(exception)) {
    return
  }
  reported.add(exception)
  try {
    sink?.trackException({ exception, properties })
  } catch {
    // A lost report is cheaper than breaking the handler that is showing the failure.
  }
}

/**
 * Reports an unexpected error that is being shown to the user. Its own text is hidden behind
 * the generic message, so without this nobody learns what broke. The report waits for the
 * end of the tick: a reporter with more context that runs in the same handler (see
 * `trackPaymentFailure`) registers the error first, and this one stands down.
 */
export const reportShownError = (error: unknown, sink: ExceptionSink) => {
  if (!isUnexpectedError(error) || reported.has(error) || queued.has(error)) {
    return
  }
  queued.add(error)
  queueMicrotask(() => send(error, sink, { source: 'shown-error' }))
}

/** Reports an error nothing caught: `info` is Vue's description of where it was raised. */
export const reportUncaughtError = (error: unknown, sink: ExceptionSink, info?: string) => {
  if (typeof error === 'object' && error !== null && reported.has(error)) {
    return
  }
  send(toError(error), sink, { source: 'uncaught-error', info })
  // A value that is not an Error was wrapped above, so the value itself is registered too.
  markReported(error)
}
